import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

const IMAGE_GAP = 1;
const SCROLL_SPEED = 1.2;
const FADE_WIDTH = 60;

const MIN_IMAGE_WIDTH = 120;
const MAX_IMAGE_WIDTH_DESKTOP = 340;
const MAX_IMAGE_WIDTH_MOBILE = 200;

function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
}

const getResponsiveImageWidth = (galleryWidth, imagesLength, isMobileOrTablet) => {
  if (!galleryWidth || !imagesLength) return MIN_IMAGE_WIDTH;
  const maxPerScreen = galleryWidth < 600
    ? 1.15
    : galleryWidth < 1000
    ? 2.1
    : 4.1;
  const maxImageWidth = isMobileOrTablet
    ? MAX_IMAGE_WIDTH_MOBILE
    : MAX_IMAGE_WIDTH_DESKTOP;

  const withGap = galleryWidth - IMAGE_GAP * (imagesLength - 1);
  if (imagesLength <= maxPerScreen) {
    return Math.max(
      MIN_IMAGE_WIDTH,
      Math.min(maxImageWidth, withGap / imagesLength)
    );
  }
  return Math.max(
    MIN_IMAGE_WIDTH,
    Math.min(maxImageWidth, galleryWidth / maxPerScreen - IMAGE_GAP)
  );
};

const isMobileOrTablet = (width) => width < 1000;

const ImageGallery = ({ images }) => {
  const processedImages = images
    .filter((image) => image?.fields?.file?.url) // Filtrar imágenes válidas
    .map((image) =>
      image.fields.file.url.startsWith("https:")
        ? image.fields.file.url
        : `https:${image.fields.file.url}`
    );
  const imagesLength = processedImages.length;

  // Si no hay imágenes válidas, no renderizar la galería
  if (imagesLength === 0) {
    return null;
  }

  const theme = useTheme();
  const wrapperRef = useRef(null);
  const carouselRef = useRef(null);
  const [galleryWidth, setGalleryWidth] = useState(1024);
  const [isPaused, setIsPaused] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [lightSweepIndex, setLightSweepIndex] = useState(null);

  const windowSize = useWindowSize();
  const mobileOrTablet = isMobileOrTablet(windowSize.width);

  useEffect(() => {
    if (wrapperRef.current) {
      setGalleryWidth(wrapperRef.current.offsetWidth);
    }
  }, [windowSize.width]);

  const imageWidth = getResponsiveImageWidth(
    galleryWidth,
    imagesLength,
    mobileOrTablet
  );

  // Auto-scroll simple: avanza hasta el final y resetea al inicio (sin duplicar imágenes)
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    let frameId = null;

    function animate() {
      if (!isPaused && carousel) {
        const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
        if (carousel.scrollLeft >= maxScrollLeft) {
          // reset to start for a continuous feeling
          carousel.scrollLeft = 0;
        } else {
          carousel.scrollLeft = Math.min(maxScrollLeft, carousel.scrollLeft + SCROLL_SPEED);
        }
      }
      frameId = requestAnimationFrame(animate);
    }

    frameId = requestAnimationFrame(animate);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isPaused, processedImages.length, imageWidth, galleryWidth]);

  useEffect(() => {
    if (viewerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [viewerOpen]);

  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  const handleImageClick = (image) => {
    setViewerImage(image);
    setViewerOpen(true);
    setIsPaused(true);
  };
  const handleOverlayClose = () => {
    setViewerOpen(false);
    setViewerImage(null);
    setIsPaused(false);
  };

  // Usar solo la lista original (sin duplicación). Centramos cuando el total ocupa menos
  // que el ancho del contenedor y permitimos auto-scroll que resetea al llegar al final.
  const visibleImagesList = processedImages;
  const shouldCenter = (imageWidth * imagesLength + IMAGE_GAP * (imagesLength - 1)) < galleryWidth;

  // Light sweep per image
  const handleLightSweepStart = (index) => {
    setLightSweepIndex(index);
    setTimeout(() => setLightSweepIndex(null), 1000);
  };

  return (
    <>
      {/* Light sweep Bar keyframes */}
      <style>
        {`
          @keyframes lightSweep {
            0% { left: -60%; opacity: 0; }
            25% { left: 10%; opacity: .2; }
            50% { left: 50%; opacity: .6; }
            72% { left: 80%; opacity: .15;}
            100% { left: 140%; opacity: 0;}
          }
        `}
      </style>
      <Box
        ref={wrapperRef}
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          position: "relative",
          background: "transparent",
          py: 0,
          userSelect: "none",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Fade solo desktop */}
        {!mobileOrTablet && (
          <>
            <Box
              sx={{
                pointerEvents: "none",
                position: "absolute",
                left: 0,
                top: 0,
                width: `${FADE_WIDTH}px`,
                height: "100%",
                zIndex: 5,
                background: `linear-gradient(to right, ${theme.palette.background.default}FF, ${theme.palette.background.default}00 95%)`,
              }}
            />
            <Box
              sx={{
                pointerEvents: "none",
                position: "absolute",
                right: 0,
                top: 0,
                width: `${FADE_WIDTH}px`,
                height: "100%",
                zIndex: 5,
                background: `linear-gradient(to left, ${theme.palette.background.default}FF, ${theme.palette.background.default}00 95%)`,
              }}
            />
          </>
        )}
        {/* Carousel */}
        <Box
          ref={carouselRef}
          sx={{
            width: "100%",
            overflowX: "hidden",
            position: "relative",
            background: "transparent",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: `${IMAGE_GAP}px`,
              height: `${imageWidth}px`,
              transition: "none",
              willChange: "transform",
              justifyContent: shouldCenter ? 'center' : 'flex-start',
            }}
          >
            {visibleImagesList.map((image, i) => (
              <Box
                key={i}
                sx={{
                  width: `${imageWidth}px`,
                  height: `${imageWidth}px`,
                  flex: "0 0 auto",
                  backgroundImage: `url(${image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderRadius: "2px",
                  cursor: "pointer",
                  boxShadow:
                    "0 4px 32px 0 rgba(32, 32, 64, 0.12), 0 0.5px 2px 0 rgba(32,32,32,0.09)",
                  filter: "none",
                  position: "relative",
                  transition:
                    "transform 0.25s cubic-bezier(0.23, 1, 0.32, 1), filter 0.2s",
                  overflow: "visible",
                  "&:hover": {
                    filter: "brightness(1.12) drop-shadow(0 0 32px #fff)",
                    zIndex: 3,
                    transform: "scale(1.10)",
                  },
                }}
                onMouseEnter={() => {
                  handleMouseEnter();
                  handleLightSweepStart(i);
                }}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleImageClick(image)}
              >
                {/* Barrido de luz solo en hover de la imagen */}
                {lightSweepIndex === i && (
                  <Box
                    sx={{
                      pointerEvents: "none",
                      position: "absolute",
                      top: 0,
                      left: "-60%",
                      width: "140%",
                      height: "100%",
                      borderRadius: "2px",
                      zIndex: 10,
                      background:
                        "linear-gradient(120deg, rgba(255,255,255,0) 65%, rgba(255,255,255,0.53) 82%, rgba(255,255,255,0.02) 100%)",
                      opacity: 1,
                      animation: "lightSweep 1s cubic-bezier(0.4,0,0.2,1)",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {/* Overlay Viewer */}
      {viewerOpen && (
        <Box
          sx={{
            zIndex: 1200,
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(12,12,20,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "opacity 0.25s",
          }}
          onClick={handleOverlayClose}
        >
          <Box
            sx={{
              position: "relative",
              maxWidth: "min(92vw,800px)",
              maxHeight: "90vh",
              outline: "none",
              boxShadow: theme.shadows[6],
              borderRadius: "8px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              sx={{
                position: "absolute",
                top: "10px",
                right: "10px",
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.54)",
                "&:hover": { bgcolor: theme.palette.error.main, color: "#fff" },
                zIndex: 10,
              }}
              onClick={handleOverlayClose}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={viewerImage}
              alt="Imagen"
              style={{
                maxWidth: "min(92vw,800px)",
                maxHeight: "90vh",
                borderRadius: "8px",
                display: "block",
                boxShadow: theme.shadows[12],
              }}
              draggable={false}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

ImageGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      fields: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    })
  ).isRequired,
};

export default ImageGallery;